import React, { useState, useEffect } from 'react';
import { Navigation, MapPin, Check, Radio, Eye, Sparkles } from 'lucide-react';
import { SportIcon } from '@/components/SportIcon';

export interface SimulatedTurf {
  id: string;
  name: string;
  sport: string;
  distanceKm: number;
  activeLobbies: number;
  x: number; // percentage on radar 15-85
  y: number; // percentage on radar 15-85
}

interface InteractiveRadarScannerProps {
  selectedArea: string;
  radiusKm: number;
  onRadiusChange: (r: number) => void;
  locationCoords: { lat: number; lng: number } | null;
  locationStatus: 'idle' | 'prompting' | 'granted' | 'denied';
  onRequestLocation: () => void;
  selectedSports: string[];
}

const SAMPLE_TURFS: SimulatedTurf[] = [
  { id: 't1', name: 'Apex Box Cricket Turf', sport: 'CRICKET', distanceKm: 1.8, activeLobbies: 2, x: 62, y: 35 },
  { id: 't2', name: 'SkyTurf 5v5 Arena', sport: 'FOOTBALL', distanceKm: 3.2, activeLobbies: 1, x: 30, y: 40 },
  { id: 't3', name: 'Metro Badminton Academy', sport: 'BADMINTON', distanceKm: 2.4, activeLobbies: 3, x: 70, y: 68 },
  { id: 't4', name: 'Smash Point Pickleball Club', sport: 'PICKLEBALL', distanceKm: 4.5, activeLobbies: 1, x: 22, y: 72 },
  { id: 't5', name: 'Elite Hoops Basketball Court', sport: 'BASKETBALL', distanceKm: 6.8, activeLobbies: 2, x: 82, y: 24 },
  { id: 't6', name: 'Clay Court Tennis Hub', sport: 'TENNIS', distanceKm: 8.5, activeLobbies: 1, x: 18, y: 22 },
  { id: 't7', name: 'Sand Dunes Volleyball Court', sport: 'VOLLEYBALL', distanceKm: 12.0, activeLobbies: 1, x: 88, y: 78 },
];

export default function InteractiveRadarScanner({
  selectedArea,
  radiusKm,
  onRadiusChange,
  locationCoords,
  locationStatus,
  onRequestLocation,
  selectedSports,
}: InteractiveRadarScannerProps) {
  const [activeBlip, setActiveBlip] = useState<SimulatedTurf | null>(null);
  const [radarDegree, setRadarDegree] = useState(0);

  // Animated sweep rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setRadarDegree((prev) => (prev + 3) % 360);
    }, 30);
    return () => clearInterval(interval);
  }, []);

  // Filter turfs based on current radius
  const visibleTurfs = SAMPLE_TURFS.filter((turf) => {
    if (turf.distanceKm > radiusKm) return false;
    if (selectedSports.length > 0) {
      return selectedSports.some((s) => s.toLowerCase() === turf.sport.toLowerCase());
    }
    return true;
  });

  return (
    <div
      style={{
        background: '#09090b',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '24px',
        padding: '20px',
        color: '#ffffff',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 12px 36px rgba(0, 0, 0, 0.4)',
      }}
    >
      {/* Top Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: '#10b981',
              boxShadow: '0 0 10px #10b981',
            }}
          />
          <span style={{ fontWeight: 800, fontSize: '14px', letterSpacing: '0.02em', color: '#f4f4f5' }}>
            LIVE TURF PROXIMITY RADAR
          </span>
        </div>

        <div
          style={{
            fontSize: '11px',
            fontWeight: 800,
            background: 'rgba(124, 58, 237, 0.2)',
            border: '1px solid rgba(124, 58, 237, 0.4)',
            color: '#c084fc',
            padding: '3px 10px',
            borderRadius: '999px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Sparkles size={12} />
          {visibleTurfs.length} Active Spots in Range
        </div>
      </div>

      {/* Radar Screen Area */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '1 / 1',
          maxHeight: '260px',
          margin: '0 auto 16px',
          background: 'radial-gradient(circle, #0e121e 0%, #030712 100%)',
          borderRadius: '20px',
          border: '1px solid rgba(124, 58, 237, 0.3)',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Concentric Rings */}
        <div
          style={{
            position: 'absolute',
            width: '85%',
            height: '85%',
            borderRadius: '50%',
            border: '1px dashed rgba(124, 58, 237, 0.25)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            width: '60%',
            height: '60%',
            borderRadius: '50%',
            border: '1px solid rgba(124, 58, 237, 0.3)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            width: '35%',
            height: '35%',
            borderRadius: '50%',
            border: '1px solid rgba(124, 58, 237, 0.4)',
          }}
        />

        {/* Crosshair Axes */}
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '1px',
            background: 'rgba(255, 255, 255, 0.08)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            height: '100%',
            width: '1px',
            background: 'rgba(255, 255, 255, 0.08)',
          }}
        />

        {/* Rotating Radar Beam Sweep */}
        <div
          style={{
            position: 'absolute',
            width: '50%',
            height: '50%',
            top: 0,
            right: 0,
            transformOrigin: 'bottom left',
            transform: `rotate(${radarDegree}deg)`,
            background: 'conic-gradient(from 180deg, rgba(124, 58, 237, 0.4) 0deg, transparent 65deg)',
            pointerEvents: 'none',
          }}
        />

        {/* Radar Center (User Anchor) */}
        <div
          style={{
            position: 'relative',
            zIndex: 10,
            width: '18px',
            height: '18px',
            borderRadius: '50%',
            background: '#7c3aed',
            border: '3px solid #ffffff',
            boxShadow: '0 0 16px #7c3aed',
          }}
        />

        {/* Distance Range Indicators */}
        <div
          style={{
            position: 'absolute',
            bottom: '8px',
            right: '12px',
            fontSize: '10px',
            color: '#71717a',
            fontFamily: 'monospace',
          }}
        >
          {radiusKm} KM RANGE
        </div>

        {/* Dynamic Turf Blips */}
        {visibleTurfs.map((turf) => (
          <button
            key={turf.id}
            type="button"
            onClick={() => setActiveBlip(turf)}
            style={{
              position: 'absolute',
              top: `${turf.y}%`,
              left: `${turf.x}%`,
              transform: 'translate(-50%, -50%)',
              background: activeBlip?.id === turf.id ? '#ffffff' : '#a855f7',
              color: activeBlip?.id === turf.id ? '#09090b' : '#ffffff',
              border: '2px solid rgba(255, 255, 255, 0.7)',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 0 12px #a855f7',
              zIndex: 5,
              transition: 'transform 0.15s ease',
            }}
            title={`${turf.name} (${turf.distanceKm} km away)`}
          >
            <SportIcon sport={turf.sport} size={12} color={activeBlip?.id === turf.id ? '#09090b' : '#ffffff'} />
          </button>
        ))}

        {/* Selected Blip Detail Card Popup */}
        {activeBlip && (
          <div
            style={{
              position: 'absolute',
              bottom: '12px',
              left: '12px',
              right: '12px',
              background: 'rgba(15, 23, 42, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(168, 85, 247, 0.5)',
              borderRadius: '12px',
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              zIndex: 20,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
            }}
          >
            <div>
              <div style={{ fontSize: '12.5px', fontWeight: 800, color: '#ffffff' }}>
                {activeBlip.name}
              </div>
              <div style={{ fontSize: '10.5px', color: '#a1a1aa', marginTop: '2px' }}>
                {activeBlip.distanceKm} km away • {activeBlip.activeLobbies} open pickup lobbies
              </div>
            </div>
            <button
              type="button"
              onClick={() => setActiveBlip(null)}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                color: '#d4d4d8',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '11px',
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        )}
      </div>

      {/* Proximity Radius Controls */}
      <div style={{ marginBottom: '16px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '12px',
            marginBottom: '8px',
          }}
        >
          <span style={{ fontWeight: 700, color: '#d4d4d8' }}>Search Radius Range</span>
          <span style={{ fontWeight: 800, color: '#c084fc' }}>Within {radiusKm} km</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
          {[2, 5, 10, 25].map((r) => {
            const isSelected = radiusKm === r;
            return (
              <button
                key={r}
                type="button"
                onClick={() => onRadiusChange(r)}
                style={{
                  padding: '8px 4px',
                  borderRadius: '10px',
                  border: isSelected ? '1.5px solid #a855f7' : '1px solid rgba(255, 255, 255, 0.1)',
                  background: isSelected ? 'rgba(124, 58, 237, 0.25)' : 'rgba(255, 255, 255, 0.04)',
                  color: isSelected ? '#ffffff' : '#a1a1aa',
                  fontWeight: 800,
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                {r} km
              </button>
            );
          })}
        </div>
      </div>

      {/* Live Location / GPS Trigger */}
      <div>
        {locationStatus === 'granted' ? (
          <div
            style={{
              background: 'rgba(16, 185, 129, 0.12)',
              border: '1px solid rgba(16, 185, 129, 0.35)',
              borderRadius: '12px',
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '12px',
              fontWeight: 700,
              color: '#34d399',
            }}
          >
            <Check size={16} />
            Live Device GPS Locked • Matching Turfs in Real Time
          </div>
        ) : (
          <button
            type="button"
            onClick={onRequestLocation}
            style={{
              width: '100%',
              background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              padding: '11px 16px',
              fontSize: '13px',
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: '0 4px 14px rgba(124, 58, 237, 0.4)',
            }}
          >
            <Navigation size={15} />
            {locationStatus === 'prompting'
              ? 'Pinging GPS Radar...'
              : locationStatus === 'denied'
              ? 'Retry Live GPS Radar'
              : 'Scan My Live Location with Radar'}
          </button>
        )}
      </div>
    </div>
  );
}
